import { useEffect, useRef } from "react";
import analytics from "../../analytics";
import type { AnalyticsBooking, AnalyticsFunnelEventName, AnalyticsVenue } from "../../analytics";
import type { ConsentCategories } from "../../services/cookieConsent/types";

export interface AnalyticsConfigurationParams {
  /** `?source=` — decides whether the journey is DesignMyNight's or a partner's. */
  readonly source?: string | null;
  /** `?affiliate_id=` — always makes the journey partner owned. */
  readonly affiliateId?: string | null;
  /** `?ga_account=` — the partner's own GA4 measurement ID. */
  readonly partnerGaAccount?: string | null;
  /** `?gtm_account=` — the partner's own GTM container ID. */
  readonly partnerGtmAccount?: string | null;
  /** `?gtm_consent=` — the decision an embedding site relayed, where it did. */
  readonly hostConsent?: ConsentCategories | null;
  /** Venue context used to brand ecommerce items; may arrive after mount. */
  readonly venue?: AnalyticsVenue;
}

/**
 * Configures the analytics service for the current journey.
 *
 * Re-runs when the URL context or venue changes, so events raised later carry
 * the right ownership and venue branding. Nothing is injected until consent is
 * granted — that is handled inside the service.
 */
export function useAnalyticsConfiguration({
  source,
  affiliateId,
  partnerGaAccount,
  partnerGtmAccount,
  hostConsent,
  venue,
}: AnalyticsConfigurationParams): void {
  const venueId = venue?.id;
  const venueTitle = venue?.title;
  const venueCurrency = venue?.currency;

  useEffect(() => {
    analytics.configure({
      source,
      affiliateId,
      partnerGaAccount,
      partnerGtmAccount,
      hostConsent,
      venue: { id: venueId, title: venueTitle, currency: venueCurrency },
    });
  }, [source, affiliateId, partnerGaAccount, partnerGtmAccount, hostConsent, venueId, venueTitle, venueCurrency]);
}

export interface FunnelAnalyticsParams {
  /** Venue context; present once the widget has resolved its configuration. */
  readonly venue?: AnalyticsVenue;
  /** The chosen booking type's Collins ID. */
  readonly bookingTypeId?: string | null;
  /** The chosen booking type's name, for the ecommerce item. */
  readonly bookingTypeName?: string | null;
  /** Covers chosen by the guest. */
  readonly partySize?: number | null;
  /** Date chosen by the guest, `YYYY-MM-DD`. */
  readonly date?: string | null;
  /** Time chosen by the guest, `HH:mm`. */
  readonly time?: string | null;
  /** The pending booking Collins created for this attempt. */
  readonly pendingBookingId?: string | null;
  /** Whether the guest has moved past the initial form onto their details. */
  readonly hasReachedDetails: boolean;
}

/** The funnel values already reported, so only a change raises an event. */
type FunnelSnapshot = Partial<Record<"venueId" | "bookingTypeId" | "date" | "partySize" | "time", string | number>>;

/**
 * The funnel steps, in the order the form presents them. A step is reported
 * when its value first appears or changes.
 */
const FUNNEL_STEPS = [
  { field: "venueId", event: "select_venue" },
  { field: "bookingTypeId", event: "select_booking_type" },
  { field: "date", event: "select_booking_date" },
  { field: "partySize", event: "select_party_size" },
  { field: "time", event: "select_booking_time" },
] as const satisfies readonly { field: keyof FunnelSnapshot; event: AnalyticsFunnelEventName }[];

function reportSelections(reported: FunnelSnapshot, current: FunnelSnapshot, selection: AnalyticsBooking): void {
  for (const { field, event } of FUNNEL_STEPS) {
    const value = current[field];
    if (value === undefined || value === reported[field]) continue;

    reported[field] = value;
    void analytics.funnelStep(event, selection);
  }
}

/**
 * Raises the funnel events as the guest works through the booking form.
 *
 * `widget_loaded` is raised once the venue configuration has resolved, then a
 * `select_*` event each time one of the guest's choices appears or changes, then
 * `begin_checkout` once a pending booking exists and the guest has reached their
 * details. Values seeded from the URL are reported the same way: the guest chose
 * them on the venue's own site.
 *
 * De-duplication of `widget_loaded` and `begin_checkout` lives in the service,
 * in `sessionStorage`, so a refresh cannot re-fire either. The snapshot ref here
 * only keeps unchanged selections from re-reporting on a re-render.
 */
export function useFunnelAnalytics({
  venue,
  bookingTypeId,
  bookingTypeName,
  partySize,
  date,
  time,
  pendingBookingId,
  hasReachedDetails,
}: FunnelAnalyticsParams): void {
  const venueId = venue?.id;
  const venueCurrency = venue?.currency;
  const reported = useRef<FunnelSnapshot>({});

  useEffect(() => {
    // The widget has not finished loading until its venue configuration is in.
    if (!venueId) return;

    void analytics.widgetLoaded();

    reportSelections(
      reported.current,
      {
        venueId,
        ...(bookingTypeId ? { bookingTypeId } : {}),
        ...(date ? { date } : {}),
        ...(partySize ? { partySize } : {}),
        ...(time ? { time } : {}),
      },
      {
        typeId: bookingTypeId ?? undefined,
        typeName: bookingTypeName ?? undefined,
        numPeople: partySize ?? undefined,
        date: date ?? undefined,
        time: time ?? undefined,
        currency: venueCurrency,
      },
    );
  }, [venueId, venueCurrency, bookingTypeId, bookingTypeName, partySize, date, time]);

  useEffect(() => {
    if (!hasReachedDetails || !pendingBookingId) return;

    void analytics.beginCheckout({
      id: pendingBookingId,
      typeId: bookingTypeId ?? undefined,
      typeName: bookingTypeName ?? undefined,
      numPeople: partySize ?? undefined,
      date: date ?? undefined,
      time: time ?? undefined,
      currency: venueCurrency,
    });
  }, [hasReachedDetails, pendingBookingId, bookingTypeId, bookingTypeName, partySize, date, time, venueCurrency]);
}
